/**
 * Downscales an image file in the browser before upload. Player photos arrive
 * from cameras at 12-18MB; nothing in the system displays them above ~400px,
 * so uploads are capped at maxDimension. PNGs keep their transparency (needed
 * for background-removed mugshots); everything else re-encodes as JPEG.
 * Non-images (videos), GIFs, SVGs, and anything that fails to decode pass
 * through untouched, as does any resize that would not actually shrink the file.
 */
export async function ResizeImage(file: File, maxDimension: number = 1000): Promise<File> {
	if (!file.type.startsWith("image/") || file.type === "image/gif" || file.type === "image/svg+xml") return file;

	try {
		const bitmap = await createImageBitmap(file);
		const scale = Math.min(1, maxDimension / Math.max(bitmap.width, bitmap.height));

		const canvas = document.createElement("canvas");
		canvas.width = Math.round(bitmap.width * scale);
		canvas.height = Math.round(bitmap.height * scale);
		const context = canvas.getContext("2d");
		if (!context) return file;
		context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
		bitmap.close?.();

		const keepAlpha = file.type === "image/png";
		const type = keepAlpha ? "image/png" : "image/jpeg";
		const blob: Blob | null = await new Promise((resolve) => canvas.toBlob((b) => resolve(b), type, keepAlpha ? undefined : 0.85));

		if (!blob || blob.size >= file.size) return file;
		return new File([blob], file.name, { type });
	} catch {
		return file;
	}
}
